import { MarketplaceStatusSchema } from '@/app/pages/providers/components/ProductForm/constants'
import { z } from 'zod'

export const ProviderAcceptRejectResponseSchema = z.object({
  success: z.boolean().optional(),
  message: z.string().optional(),
})
export type ProviderAcceptRejectResponse = z.infer<
  typeof ProviderAcceptRejectResponseSchema
>

export const SendProviderMessageResponseSchema = z.object({
  success: z.boolean().optional(),
  message: z.string().optional(),
})
export type SendProviderMessageResponse = z.infer<
  typeof SendProviderMessageResponseSchema
>

export const ItemSchema = z.object({
  id: z.number(),
  status: z.string(),
  type: z.union([z.string(), z.number()]).nullable(),
  name: z.string(),
})

export const ProviderItemSchema = z.object({
  id: z.number(),
  program_item_id: z.number(),
  provider_id: z.number(),
  program_id: z.number(),
  global_item_category_id: z.number(),
  global_item_id: z.number(),
  status: z.number(),
  justification: z.string().nullable(),
  marketplace: MarketplaceStatusSchema,
  name: z.string(),
  type: z.union([z.string(), z.number()]).nullable(),
  price: z.number(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  specific_duration: z.number().nullable(),
  duration_unit: z.string().nullable(),
  usage_quantity: z.number().nullable(),
  sales_tax: z.string().nullable(),
  subscription: z.number().nullable().or(z.undefined()),
  comments: z.string().nullable(),
  keyword_tags: z.string().nullable(),
  image: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  status_name: z.string(),
  item: ItemSchema,
})

export const ProviderItemCategorySchema = z.object({
  id: z.number(),
  status: z.string(),
  type: z.union([z.string(), z.number()]).nullable(),
  name: z.string(),
  provider_items: z.array(ProviderItemSchema),
})

export const ProviderItemCategoryListSchema = z.array(
  ProviderItemCategorySchema
)

export type Item = z.infer<typeof ItemSchema>
export type ProviderItem = z.infer<typeof ProviderItemSchema>
export type ProviderItemCategory = z.infer<typeof ProviderItemCategorySchema>
export type ProviderItemCategoryList = z.infer<
  typeof ProviderItemCategoryListSchema
>

export const ProviderItemAcceptRejectResponseSchema = z.object({
  success: z.boolean().optional(),
  message: z.string().optional(),
})

export type ProviderItemAcceptRejectResponse = z.infer<
  typeof ProviderItemAcceptRejectResponseSchema
>

export const SchoolApplicationBaseSchema = z.object({
  id: z.number(),
  provider_id: z.number(),
  program_id: z.number(),
  school_name: z.string(),
  street_address: z.string(),
  street_address_line_2: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  postal_zip_code: z.string(),
  is_mailing_address_same: z.string().nullable(),
  mailing_street_address: z.string().nullable(),
  mailing_street_address_line_2: z.string().nullable(),
  mailing_city: z.string().nullable(),
  mailing_state: z.string().nullable(),
  mailing_zip_code: z.string().nullable(),
  school_area_location: z.string().nullable(),
  lowest_grade_served: z.string().nullable(),
  highest_grade_served: z.string().nullable(),
  religious_affiliation: z.string().nullable(),
  coed_status: z.string().nullable(),
  hybrid_status: z.string().nullable(),
  areas_of_specialization: z.string().nullable(),
  learning_differences_accommodated: z.string().nullable(),
  additional_programs_offered: z.string().nullable(),
  accreditation_bodies: z.string().nullable(),
  charges_tuition: z.string().nullable(),
  pre_k_tuition: z.string().nullable(),
  kindergarten_tuition: z.string().nullable(),
  elementary_tuition: z.string().nullable(),
  middle_school_tuition: z.string().nullable(),
  high_school_tuition: z.string().nullable(),
  variable_tuition_forms: z.string().nullable(),
  per_pupil_expenditures: z.string().nullable(),
  administers_standardized_test: z.string().nullable(),
  standardized_tests_administered: z.string().nullable(),
  student_assessment_methods: z.string().nullable(),
  contracted_with_facts: z.string().nullable(),
  maximum_school_capacity: z.string().nullable(),
  current_enrollment: z.string().nullable(),
  percentage_350_poverty_guidelines: z.string().nullable(),
  additional_students_350_poverty: z.string().nullable(),
  student_teacher_ratio_full_time: z.string().nullable(),
  student_teacher_ratio_all: z.string().nullable(),
  primary_contact_first_name: z.string().nullable(),
  primary_contact_last_name: z.string().nullable(),
  primary_contact_title: z.string().nullable(),
  primary_contact_phone: z.string().nullable(),
  primary_contact_email: z.string().nullable(),
  primary_contact_role: z.string().nullable(),
  gender_female: z.string().nullable(),
  gender_male: z.string().nullable(),
  gender_other: z.string().nullable(),
  ethnicity_african_american: z.string().nullable(),
  ethnicity_asian: z.string().nullable(),
  ethnicity_caucasian: z.string().nullable(),
  ethnicity_east_asian: z.string().nullable(),
  ethnicity_hispanic_latino: z.string().nullable(),
  ethnicity_middle_eastern: z.string().nullable(),
  ethnicity_multiracial: z.string().nullable(),
  ethnicity_native_american: z.string().nullable(),
  ethnicity_native_hawaiian: z.string().nullable(),
  ethnicity_prefer_not_to_say: z.string().nullable(),
  percentage_esl_students: z.string().nullable(),
  percentage_esl_parents: z.string().nullable(),
  new_school_to_ace: z.string().nullable(),
  year_established: z.string().nullable(),
  history_of_school: z.string().nullable(),
  school_website: z.string().nullable(),
  online_information_source: z.string().nullable(),
  mission_statement: z.string().nullable(),
  school_picture: z.string().nullable(),
  receives_ach_payment: z.string().nullable(),
  federal_tax_id: z.string().nullable(),
  account_type: z.string().nullable(),
  depository_name: z.string().nullable(),
  bank_branch: z.string().nullable(),
  bank_street_address: z.string().nullable(),
  bank_street_address_line_2: z.string().nullable(),
  bank_city: z.string().nullable(),
  bank_state: z.string().nullable(),
  bank_routing_id: z.string().nullable(),
  account_number: z.string().nullable(),
  receive_physical_checks: z.string().nullable(),
  attestation: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

export const SchoolApplicationResponseSchema = SchoolApplicationBaseSchema
export type SchoolApplicationResponse = z.infer<
  typeof SchoolApplicationResponseSchema
>

export const UpdateSchoolApplicationSchema =
  SchoolApplicationBaseSchema.partial()
export type UpdateSchoolApplicationPayload = z.infer<
  typeof UpdateSchoolApplicationSchema
>

export const ImportProviderErrorSchema = z.object({
  index: z.number(),
  provider: z.string().nullable(),
  phone: z.union([z.string(), z.number()]),
  errors: z.array(z.string()),
})

export const ImportProvidersResponseSchema = z.object({
  message: z.string(),
  total_processed: z.number(),
  total_successfully_imported: z.number(),
  total_failed_imported: z.number(),
  errors: z.array(ImportProviderErrorSchema).optional(),
  download_file: z.string().optional(),
})

export type ImportProvidersResponse = z.infer<
  typeof ImportProvidersResponseSchema
>
